// Double Ratchet for Nexus E2E (Signal spec), giving forward secrecy and
// post-compromise recovery (КР-4). DH ratchet via X25519, symmetric chains via
// HMAC-SHA256, message encryption via the ozcrypto AES-256-GCM core (О-ГР-1).
//
// State is a plain object of Uint8Arrays so it can be serialized to IndexedDB.
// The AEAD instance (ozcrypto wrapper) is injected so this file stays free of
// any symmetric-crypto implementation.

import { dh as x25519dh, generateDHKeyPair, kdf, mac } from './asym.js';

const MAX_SKIP = 1000; // bound on skipped message keys (DoS guard — ФТ-6)

const concat = (...arrs) => {
  let n = 0; for (const a of arrs) n += a.length;
  const out = new Uint8Array(n); let o = 0;
  for (const a of arrs) { out.set(a, o); o += a.length; }
  return out;
};
const u32be = (n) => new Uint8Array([(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255]);
const hex = (b) => Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');

// Root KDF: (rk, dh_out) -> (rk', ck).
function kdfRK(rk, dhOut) {
  const out = kdf(dhOut, rk, 'NexusRatchetRK', 64);
  return { rk: out.slice(0, 32), ck: out.slice(32, 64) };
}
// Chain KDF: ck -> (ck', mk).
function kdfCK(ck) {
  const mk = mac(ck, new Uint8Array([0x01]));
  const nck = mac(ck, new Uint8Array([0x02]));
  return { ck: nck, mk };
}
// Message key -> AES-256 key (32) + GCM nonce (12). Each mk is used once, so the
// derived (key,nonce) pair is unique — satisfies GCM nonce uniqueness (КР-2).
function messageKeys(mk) {
  const out = kdf(mk, new Uint8Array(32), 'NexusRatchetMK', 44);
  return { key: out.slice(0, 32), iv: out.slice(32, 44) };
}

// Serialize the header (dh pub || PN || N) — also fed into the AEAD AAD (КР-7).
function headerBytes(h) { return concat(h.dh, u32be(h.pn), u32be(h.n)); }

// --- Initialization ---

// Initiator (Alice): she already knows the responder's initial ratchet public
// key (his signed prekey). SK comes from X3DH.
export function initAlice(sk, theirRatchetPub) {
  const dhs = generateDHKeyPair();
  const { rk, ck } = kdfRK(sk, x25519dh(dhs.priv, theirRatchetPub));
  return {
    dhs, dhr: theirRatchetPub, rk, cks: ck, ckr: null,
    ns: 0, nr: 0, pn: 0, skipped: {},
  };
}

// Responder (Bob): his initial ratchet key pair is his signed prekey. He has no
// remote ratchet key until Alice's first message arrives.
export function initBob(sk, ratchetKeyPair) {
  return {
    dhs: ratchetKeyPair, dhr: null, rk: sk, cks: null, ckr: null,
    ns: 0, nr: 0, pn: 0, skipped: {},
  };
}

// --- Encrypt ---

export function ratchetEncrypt(st, plaintext, ad, aead) {
  const step = kdfCK(st.cks);
  st.cks = step.ck;
  const header = { dh: st.dhs.pub, pn: st.pn, n: st.ns };
  st.ns += 1;
  const { key, iv } = messageKeys(step.mk);
  const fullAad = concat(ad, headerBytes(header));
  const { ct, tag } = aead.encrypt(key, iv, fullAad, plaintext);
  return { header, ct, tag };
}

// --- Decrypt ---

function trySkipped(st, header, ct, tag, ad, aead) {
  const id = hex(header.dh) + ':' + header.n;
  const mk = st.skipped[id];
  if (!mk) return undefined;
  const { key, iv } = messageKeys(mk);
  const pt = aead.decrypt(key, iv, concat(ad, headerBytes(header)), ct, tag);
  if (pt) delete st.skipped[id];
  return pt; // may be null if tag fails
}

function skipMessageKeys(st, until) {
  if (st.ckr == null) return;
  if (st.nr + MAX_SKIP < until) throw new Error('E2E: too many skipped messages');
  while (st.nr < until) {
    const step = kdfCK(st.ckr);
    st.ckr = step.ck;
    st.skipped[hex(st.dhr) + ':' + st.nr] = step.mk;
    st.nr += 1;
  }
}

function dhRatchet(st, header) {
  st.pn = st.ns; st.ns = 0; st.nr = 0;
  st.dhr = header.dh;
  let r = kdfRK(st.rk, x25519dh(st.dhs.priv, st.dhr));
  st.rk = r.rk; st.ckr = r.ck;
  st.dhs = generateDHKeyPair();
  r = kdfRK(st.rk, x25519dh(st.dhs.priv, st.dhr));
  st.rk = r.rk; st.cks = r.ck;
}

// Returns the plaintext Uint8Array, or null if authentication fails (ФТ-5).
export function ratchetDecrypt(st, header, ct, tag, ad, aead) {
  const fromSkipped = trySkipped(st, header, ct, tag, ad, aead);
  if (fromSkipped !== undefined) return fromSkipped;

  const sameDhr = st.dhr && hex(st.dhr) === hex(header.dh);
  if (!sameDhr) {
    skipMessageKeys(st, header.pn);
    dhRatchet(st, header);
  }
  skipMessageKeys(st, header.n);
  const step = kdfCK(st.ckr);
  st.ckr = step.ck;
  st.nr += 1;
  const { key, iv } = messageKeys(step.mk);
  return aead.decrypt(key, iv, concat(ad, headerBytes(header)), ct, tag);
}
